import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and organization context to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  let orgId = localStorage.getItem('activeOrganizationId');
  if (!orgId) {
    const storedUser = localStorage.getItem('user');
    if (storedUser && storedUser !== "undefined") {
      try {
        const user = JSON.parse(storedUser);
        orgId = user.organization_id;
      } catch (e) {}
    }
  }
  
  if (orgId) {
    config.headers['X-Organization-ID'] = orgId;
  }
  
  return config;
});

// Handle auth errors and subscription tier gates
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Only redirect if not already on login page to avoid infinite refresh/clearing error state
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } else if (error.response?.status === 402) {
      // Dispatch custom event to show upgrade dialog
      window.dispatchEvent(new CustomEvent('upgrade-required', {
        detail: { 
          message: error.response.data?.detail || 'Upgrade erforderlich.',
          feature: error.config?.url || ''
        }
      }));
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email, password, workspace_id = null) => api.post('/auth/login', { email, password, workspace_id }),
  verifyTwoFactor: (token, code) => api.post('/auth/2fa/verify', { token, code }),
  setupTwoFactor: () => api.post('/auth/2fa/setup'),
  enableTwoFactor: (code) => api.post('/auth/2fa/enable', { code }),
  disableTwoFactor: (password) => api.post('/auth/2fa/disable', { password }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (old_password, new_password) => api.post('/auth/change-password', { old_password, new_password }),
};

export const organizationsApi = {
  getMe: () => api.get('/organizations/me'),
  getAll: () => api.get('/organizations'),
  create: (data) => api.post('/organizations', data),
  delete: (id) => api.delete(`/organizations/${id}`),
};

// Users (Property Managers)
export const usersApi = {
  getAll: () => api.get('/users'),
};

// Owners
export const ownersApi = {
  getAll: (params) => api.get('/owners', { params }),
  getById: (id) => api.get(`/owners/${id}`),
  getDetails: (id) => api.get(`/owners/${id}/details`),
  create: (data) => api.post('/owners', data),
  update: (id, data) => api.put(`/owners/${id}`, data),
  delete: (id) => api.delete(`/owners/${id}`),
  archive: (id) => api.post(`/owners/${id}/archive`),
  unarchive: (id) => api.post(`/owners/${id}/unarchive`),
  analyzeImport: (immowareFile, dkbFile) => {
    const formData = new FormData();
    formData.append('immoware_file', immowareFile);
    formData.append('dkb_file', dkbFile);
    return api.post('/owners/import/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  executeImport: (owners) => api.post('/owners/import/execute', { owners }),
};

// Apartments
export const apartmentsApi = {
  getAll: (params) => api.get('/apartments', { params }),
  getById: (id) => api.get(`/apartments/${id}`),
  create: (data) => api.post('/apartments', data),
  update: (id, data) => api.patch(`/apartments/${id}`, data),
  fullUpdate: (id, data) => api.put(`/apartments/${id}`, data),
  delete: (id) => api.delete(`/apartments/${id}`),
  archive: (id) => api.post(`/apartments/${id}/archive`),
  unarchive: (id) => api.post(`/apartments/${id}/unarchive`),
  getFinancials: (id, params) => api.get(`/apartments/${id}/financials`, { params }),
  getStakeholders: (id) => api.get(`/apartments/${id}/stakeholders`),
  getRentStatus: (id, year) => api.get(`/apartments/${id}/rent-status`, { params: { year } }),
  getExpenseSummary: (id, params) => api.get(`/apartments/${id}/expense-summary`, { params }),
  getValueHistory: (id) => api.get(`/apartments/${id}/value-history`),
  getYearlyOverview: (id, year) => api.get(`/apartments/${id}/yearly-overview`, { params: { year } }),
  setManualRentTarget: (id, month, amount) => api.patch(`/apartments/${id}/rent-target`, { month, amount }),
  getRentReportPdf: (id, params) => api.get(`/apartments/${id}/rent-report-pdf`, { params, responseType: 'arraybuffer' }),
  bulkUpdateApartments: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/apartments/import/update', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importPropertyManagers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/apartments/import/property-managers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const locationApi = {
  geocodeSearch: (q, limit = 5) => api.get('/location/geocode', { params: { q, limit } }),
  geocodeApartment: (id) => api.post(`/location/apartments/${id}/geocode`),
  detectLocation: (id) => api.post(`/location/apartments/${id}/detect`),
  overrideLocation: (id, data) => api.post(`/location/apartments/${id}/override`, data),
  batchDetect: () => api.post('/location/detect/batch'),
  getDistrictMappings: () => api.get('/location/district-mappings'),
  updateDistrictMappings: (mappings) => api.put('/location/district-mappings', { mappings }),
  getZones: (zone_type) => api.get('/location/zones', { params: zone_type ? { zone_type } : {} }),
  createZone: (data) => api.post('/location/zones', data),
  updateZone: (id, data) => api.put(`/location/zones/${id}`, data),
  deleteZone: (id) => api.delete(`/location/zones/${id}`),
  ingestZones: (data) => api.post('/location/zones/ingest', data),
};

export const mietspiegelApi = {
  getDashboard: () => api.get('/mietspiegel/dashboard'),
  getApartmentAnalysis: (id) => api.get(`/mietspiegel/apartments/${id}`),
  lookup: (params) => api.get('/mietspiegel/lookup', { params }),
  setLastIncrease: (id, date, rentBefore = null) => api.patch(`/mietspiegel/apartments/${id}/last-increase`, {
    last_rent_increase_date: date,
    ...(rentBefore != null && { rent_before_last_increase: rentBefore }),
  }),
  sendReminder: (id, data) => api.post(`/mietspiegel/apartments/${id}/remind`, data),
};

export const rentIncreaseApi = {
  createCase: (data) => api.post('/rent-increase/cases', data),
  listCases: (params) => api.get('/rent-increase/cases', { params }),
  getCase: (id) => api.get(`/rent-increase/cases/${id}`),
  adjustCase: (id, data) => api.patch(`/rent-increase/cases/${id}`, data),
  generateDocument: (id) => api.post(`/rent-increase/cases/${id}/generate-document`),
  downloadDocument: (id) => api.get(`/rent-increase/cases/${id}/document`, { responseType: 'blob' }),
  approveCase: (id) => api.post(`/rent-increase/cases/${id}/approve`),
  markSent: (id, data) => api.post(`/rent-increase/cases/${id}/mark-sent`, data),
  previewDocument: (id) => api.get(`/rent-increase/cases/${id}/preview`, { responseType: 'blob' }),
  deleteCase: (id) => api.delete(`/rent-increase/cases/${id}`),
};

// Tenants
export const tenantsApi = {
  getAll: (params) => api.get('/tenants', { params }),
  getById: (id) => api.get(`/tenants/${id}`),
  getDetails: (id) => api.get(`/tenants/${id}/details`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  delete: (id) => api.delete(`/tenants/${id}`),
  archive: (id) => api.post(`/tenants/${id}/archive`),
  unarchive: (id) => api.post(`/tenants/${id}/unarchive`),
};

// Contracts
export const contractsApi = {
  getAll: (params) => api.get('/contracts', { params }),
  getById: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  getValueHistory: (id) => api.get(`/contracts/${id}/value-history`),
  delete: (id) => api.delete(`/contracts/${id}`),
};

// Payments
export const paymentsApi = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  generateExpected: (params) => api.post('/payments/generate-expected', null, { params }),
  matchTransaction: (paymentId, transactionId) => api.post(`/payments/${paymentId}/match-transaction`, null, { params: { transaction_id: transactionId } }),
  autoMatch: (params) => api.post('/payments/auto-match', null, { params }),
};

// Bank Transactions
export const bankTransactionsApi = {
  getAll: (params) => api.get('/bank-transactions', { params }),
  create: (data) => api.post('/bank-transactions', data),
  update: (id, data) => api.put(`/bank-transactions/${id}`, null, { params: data }),
  previewCsv: (file, ownerId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bank-transactions/preview-csv', formData, {
      params: { owner_id: ownerId },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importCsv: (file, ownerId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bank-transactions/import-csv', formData, {
      params: { owner_id: ownerId },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  categorize: (id) => api.post(`/bank-transactions/${id}/categorize`),
  categorizeAll: (ownerId, limit = 20) => api.post('/bank-transactions/categorize-all', null, {
    params: { ...(ownerId ? { owner_id: ownerId } : {}), limit }
  }),
  rematchAll: (ownerId, forceAll) => api.post('/bank-transactions/rematch-all', null, { params: { ...(ownerId ? { owner_id: ownerId } : {}), ...(forceAll ? { force_all: true } : {}) } }),
  rematchAllEnhanced: (params) => api.post('/bank-transactions/rematch-all-enhanced', null, { params }),
  
  // Optimized paginated endpoints
  getByOwner: (ownerId, params) => api.get(`/owners/${ownerId}/transactions`, { params }),
  getByApartment: (apartmentId, params) => api.get(`/apartments/${apartmentId}/transactions`, { params }),
  
  // Matching endpoints
  autoMatch: (id) => api.post(`/bank-transactions/${id}/auto-match`),
  batchMatch: (params, transactionIds) => api.post('/bank-transactions/batch-match', 
    transactionIds ? { transaction_ids: transactionIds } : null, 
    { params }
  ),
  batchClassify: (data) => api.post('/bank-transactions/batch-classify', data),
  batchDelete: (transactionIds) => api.post('/bank-transactions/batch-delete', { transaction_ids: transactionIds }),
  delete: (id) => api.delete(`/bank-transactions/${id}`),
  smartMatchByReference: (params) => api.post('/bank-transactions/smart-match-by-reference', null, { params }),
  getMatchBreakdown: (id) => api.get(`/bank-transactions/${id}/match-breakdown`),
  getMatchOptions: (id) => api.get(`/bank-transactions/${id}/match-options`),
  applyMatchOption: (id, data) => api.post(`/bank-transactions/${id}/apply-match-option`, data),
  rematchSingle: (id, dryRun = false) => api.post(`/bank-transactions/${id}/rematch`, null, { params: { dry_run: dryRun } }),
  assignToApartment: (id, data) => api.post(`/bank-transactions/${id}/assign`, data),
  unassign: (id) => api.post(`/bank-transactions/${id}/unassign`),
  assignAsRent: (id, body) => api.post(`/bank-transactions/${id}/assign-as-rent`, body),
  reclassifyWohngeld: (dryRun = false) => api.post('/bank-transactions/reclassify-wohngeld', null, { params: { dry_run: dryRun } }),
  
  // BANKSapi endpoints
  banksapiTest: () => api.post('/bank-transactions/banksapi/test'),
  banksapiInitiate: (ownerId, callbackUrl) => api.post('/bank-transactions/banksapi/initiate', null, {
    params: { owner_id: ownerId, callback_url: callbackUrl }
  }),
  banksapiCallback: (bankzugangId, ownerId) => api.post('/bank-transactions/banksapi/callback', null, {
    params: { bankzugang_id: bankzugangId, owner_id: ownerId }
  }),
  banksapiListAccesses: (ownerId) => api.get('/bank-transactions/banksapi/accesses', { 
    params: ownerId ? { owner_id: ownerId } : {} 
  }),
  banksapiSync: (params) => api.post('/bank-transactions/banksapi/sync', null, { params }),
};

// IBAN Connections
export const ibanConnectionsApi = {
  getAll: () => api.get('/iban-connections'),
  create: (data) => api.post('/iban-connections', data),
  delete: (id, unassignTransactions = false) => api.delete(`/iban-connections/${id}`, { params: { unassign_transactions: unassignTransactions } }),
  fromTransaction: (txnId, params) => api.post(`/iban-connections/from-transaction/${txnId}`, null, { params }),
};

// Settings
export const settingsApi = {
  setBanksapiKey: (apiKey) => api.post('/settings/banksapi-key', null, { params: { api_key: apiKey } }),
};

export const remindersApi = {
  getAll: (params) => api.get('/reminders', { params }),
  create: (data) => api.post('/reminders', data),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  delete: (id) => api.delete(`/reminders/${id}`),
  autoGenerate: (apartmentIds = null) => api.post('/reminders/auto-generate', apartmentIds ? apartmentIds : null),
  batchApprove: (reminderIds, sendPostal = false) => api.post('/reminders/batch-approve', { reminder_ids: reminderIds, send_postal: sendPostal }),
  batchDismiss: (reminderIds) => api.post('/reminders/batch-dismiss', { reminder_ids: reminderIds }),
  retryDispatch: (id) => api.patch(`/reminders/${id}/retry-dispatch`),
  generateTerminationNotice: (id) => api.post(`/reminders/${id}/generate-termination-notice`, null, { responseType: 'blob' }),
};

// Hausverwaltungen
export const hausverwaltungenApi = {
  getAll: () => api.get('/hausverwaltungen'),
  create: (data) => api.post('/hausverwaltungen', data),
  update: (id, data) => api.put(`/hausverwaltungen/${id}`, data),
  delete: (id) => api.delete(`/hausverwaltungen/${id}`),
};

// Service Providers
export const serviceProvidersApi = {
  getAll: () => api.get('/service-providers'),
  create: (data) => api.post('/service-providers', data),
  update: (id, data) => api.put(`/service-providers/${id}`, data),
  delete: (id) => api.delete(`/service-providers/${id}`),
};

// Tasks
export const tasksApi = {
  getAll: (params) => api.get('/tasks', { params }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Tickets (Repair & Issue Tracking)
export const ticketsApi = {
  getAll: (params) => api.get('/tickets', { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  getStats: () => api.get('/tickets/stats'),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  patch: (id, data) => api.patch(`/tickets/${id}`, data),
  addComment: (id, text) => api.post(`/tickets/${id}/comments`, { text }),
  delete: (id) => api.delete(`/tickets/${id}`),
  // Attachments
  uploadAttachment: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tickets/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAttachmentUrl: (ticketId, attachmentId) => `${API_BASE}/tickets/${ticketId}/attachments/${attachmentId}`,
  deleteAttachment: (ticketId, attachmentId) => api.delete(`/tickets/${ticketId}/attachments/${attachmentId}`),
};

// Payment Orders
export const paymentOrdersApi = {
  getAll: (params) => api.get('/payment-orders', { params }),
  getById: (id) => api.get(`/payment-orders/${id}`),
  create: (data) => api.post('/payment-orders', data),
  update: (id, data) => api.put(`/payment-orders/${id}`, data),
  delete: (id) => api.delete(`/payment-orders/${id}`),
  generate: (params) => api.post('/payment-orders/generate', null, { params }),
  markPaid: (id, paidDate) => api.post(`/payment-orders/${id}/mark-paid`, null, { params: { paid_date: paidDate } }),
  sendToBank: (id) => api.post(`/payment-orders/${id}/initiate-payment`),
  confirmTransferTan: (orderId, sessionKey, tanCode) =>
    api.post(`/payment-orders/${orderId}/confirm-tan?session_key=${sessionKey}${tanCode ? '&tan_code=' + encodeURIComponent(tanCode) : ''}`),
  batchSendToBank: (orderIds) => api.post('/payment-orders/batch-send-to-bank', { order_ids: orderIds }),
  batchConfirmTan: (sessionKey, tanCode) =>
    api.post(`/payment-orders/batch-confirm-tan?session_key=${sessionKey}${tanCode ? '&tan_code=' + encodeURIComponent(tanCode) : ''}`),
  batchDelete: (orderIds) => api.post('/payment-orders/batch-delete', { order_ids: orderIds }),
};

// Reports
export const reportsApi = {
  getOwnerSummary: (params) => api.get('/reports/owner-summary', { params }),
  downloadMonthly: (params) => api.get('/reports/owner-monthly', { params, responseType: 'blob' }),
  downloadAnnual: (params) => api.get('/reports/owner-annual', { params, responseType: 'blob' }),
  downloadOwnerPortfolio: (params) => api.get('/reports/owner-portfolio', { params, responseType: 'blob' }),
  downloadStammdaten: (params) => api.get('/reports/apartment-stammdaten', { params, responseType: 'blob' }),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getLatePayments: () => api.get('/dashboard/late-payments'),
  getExpiringContracts: () => api.get('/dashboard/expiring-contracts'),
};

// MS365 Email
export const emailApi = {
  getStatus: () => api.get('/auth/ms365/status'),
  getLoginUrl: () => api.get('/auth/ms365/login'),
  disconnect: () => api.post('/auth/ms365/disconnect'),
  send: (data) => api.post('/email/send', data),
  sendReport: (data) => api.post('/email/send-report', data),
  sendAllReports: (data) => api.post('/email/send-all-reports', data),
  getLogs: (params) => api.get('/email/logs', { params }),
};

// Documents
export const documentsApi = {
  getAll: (params) => api.get('/documents', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  getTemplates: () => api.get('/documents/templates'),
  duplicate: (id) => api.post(`/documents/${id}/duplicate`),
  generateFromTemplate: (params) => api.post('/documents/generate-from-template', null, { params }),
  seedTemplates: () => api.post('/documents/seed-templates'),
  upload: (formData, params) => api.post('/documents/upload', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    params 
  }),
  getPreviewUrl: (id) => `${API_BASE}/documents/${id}/preview`,
  downloadPreview: (id) => api.get(`/documents/${id}/preview`, { responseType: 'blob' }),
  getProcessed: (params) => api.get('/documents/processed', { params }),
  getProcessingDetails: (id) => api.get(`/documents/${id}/processing`),
  getEntityLinks: (id) => api.get(`/documents/${id}/entity-links`),
  triggerIngestion: () => api.post('/documents/trigger-ingestion'),
};
 
export const mieterhoehungApi = {
  listProposals: (params) => api.get('/mieterhoehung/proposals', { params }),
  getProposal: (id) => api.get(`/mieterhoehung/proposals/${id}`),
  approveProposal: (id) => api.post(`/mieterhoehung/proposals/${id}/approve`),
};

// Utility Settlements (Nebenkostenabrechnung)
export const utilitySettlementsApi = {
  getAll: (params) => api.get('/utility-settlements', { params }),
  getById: (id) => api.get(`/utility-settlements/${id}`),
  createFromWohngeldabrechnung: (documentId, apartmentId, year) => 
    api.post('/utility-settlements/create-from-wohngeldabrechnung', null, {
      params: { document_id: documentId, apartment_id: apartmentId, settlement_year: year }
    }),
  update: (id, data) => api.put(`/utility-settlements/${id}`, data),
  generateDocument: (id) => api.post(`/utility-settlements/${id}/generate-document`),
  delete: (id) => api.delete(`/utility-settlements/${id}`),
  attachHeatingPdf: (id, formData) => api.post(`/utility-settlements/${id}/attach-heating-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Dropbox Integration
export const dropboxApi = {
  getStatus: () => api.get('/dropbox/status'),
  listFiles: (subfolder = '') => api.get('/dropbox/files', { params: { subfolder } }),
  getLink: (dropboxPath) => api.post('/dropbox/get-link', null, { params: { dropbox_path: dropboxPath } }),
};

// Pingen Postal Service
export const pingenApi = {
  getStatus: () => api.get('/pingen/status'),
  sendDocument: (data) => api.post('/pingen/send', data),
  getLetterStatus: (letterId) => api.get(`/pingen/letter/${letterId}`),
};

// Admin Tools
export const adminApi = {
  migrateTransactions: () => api.post('/admin/migrate-transactions'),
  getTransactionStats: () => api.get('/admin/transaction-stats'),
};

// Ledger (Hauptbuch)
export const ledgerApi = {
  getEntries: (params) => api.get('/ledger', { params }),
  getStats: (params) => api.get('/ledger/stats', { params }),
  reconcile: (params) => api.post('/ledger/reconcile', null, { params }),
  reconcileStart: (params) => api.post('/ledger/reconcile/start', null, { params }),
  reconcileContinue: (jobId) => api.post(`/ledger/reconcile/${jobId}/continue`),
  reconcileStatus: (jobId) => api.get(`/ledger/reconcile/status/${jobId}`),
  export: (params) => api.get('/ledger/export', { params, responseType: params.format === 'csv' ? 'blob' : 'json' }),
};

// FinAPI Integration
export const finApiApi = {
  testConnection: () => api.get('/finapi/test'),
  saveCredentials: (data) => api.post('/settings/finapi', data),
  createUser: (ownerId) => api.post('/finapi/users', null, { params: { owner_id: ownerId } }),
  initiateBankConnection: (ownerId) => api.post('/finapi/bank-connections/import', null, { params: { owner_id: ownerId } }),
  getBankConnectionStatus: (webformId) => api.get(`/finapi/bank-connections/status/${webformId}`),
  getBankConnections: (ownerId) => api.get('/finapi/bank-connections', { params: { owner_id: ownerId } }),
  deleteBankConnection: (connectionId, ownerId) => api.delete(`/finapi/bank-connections/${connectionId}`, { params: { owner_id: ownerId } }),
  syncTransactions: (ownerId, bankConnectionId, fromDate) => api.post('/finapi/sync-transactions', null, { 
    params: { 
      owner_id: ownerId, 
      ...(bankConnectionId ? { bank_connection_id: bankConnectionId } : {}),
      ...(fromDate ? { from_date: fromDate } : {})
    } 
  }),
};

// Migration
export const migrationApi = {
  analyze: (dkbFile, tenantsFile) => {
    const formData = new FormData();
    formData.append('dkb_file', dkbFile);
    if (tenantsFile) {
      formData.append('tenants_file', tenantsFile);
    }
    return api.post('/migration/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  import: (data) => api.post('/migration/import', data, { timeout: 120000 }),
  clearAll: () => api.delete('/migration/clear-all'),
  dryRunWorkbook: (workbookFile, targetOrgName, targetOrgId) => {
    const formData = new FormData();
    formData.append('workbook_file', workbookFile);
    if (targetOrgName) formData.append('target_org_name', targetOrgName);
    if (targetOrgId) formData.append('target_org_id', targetOrgId);
    return api.post('/migration/workbook/dry-run', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  importWorkbook: (workbookFile, targetOrgName, targetOrgId, confirmation, ownerMappings, apartmentMappings) => {
    const formData = new FormData();
    formData.append('workbook_file', workbookFile);
    if (targetOrgName) formData.append('target_org_name', targetOrgName);
    if (targetOrgId) formData.append('target_org_id', targetOrgId);
    formData.append('confirmation', confirmation);
    if (ownerMappings) formData.append('owner_mappings', JSON.stringify(ownerMappings));
    if (apartmentMappings) formData.append('apartment_mappings', JSON.stringify(apartmentMappings));
    return api.post('/migration/workbook/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
};


// FinTS/HBCI Banking (DKB direct)
export const fintsApi = {
  connect: (data) => api.post('/banking/fints/connect', data),
  confirmTan: (data) => api.post('/banking/fints/confirm-tan', data),
  sync: (connectionId) => api.post(`/banking/fints/sync/${connectionId}`),
  syncGlobal: (connectionId) => api.post(`/banking/fints/sync-global/${connectionId}`),
  syncOwner: (connectionId, ownerId) => api.post(`/banking/fints/sync-owner/${connectionId}/${ownerId}`),
  getSyncStatus: (syncJobId) => api.get(`/banking/fints/sync-status/${syncJobId}`),
  getGlobalConnection: () => api.get('/banking/fints/global-connection'),
  getAllConnections: () => api.get('/banking/fints/all-connections'),
  getOwnerStatus: (ownerId) => api.get(`/banking/fints/owner-status/${ownerId}`),
  listConnections: (ownerId) => api.get(`/banking/fints/connections/${ownerId}`),
  getStatus: (connectionId) => api.get(`/banking/fints/status/${connectionId}`),
  deleteConnection: (connectionId) => api.delete(`/banking/fints/connections/${connectionId}`),
  toggleAutoSync: (connectionId, enabled) => api.patch(`/banking/fints/connections/${connectionId}/auto-sync`, null, { params: { enabled } }),
  resumeAutoSync: (connectionId) => api.post(`/banking/fints/connections/${connectionId}/resume-auto-sync`),
  getSchedulerStatus: () => api.get('/banking/fints/scheduler/status'),
};

export const financeApi = {
  getOverview: (month) => api.get('/finance/overview', { params: { month } }),
  getDataQuality: () => api.get('/finance/data-quality'),
  getBouncedPayments: (params) => api.get('/finance/bounced-payments', { params }),
};

export const transactionRulesApi = {
  getAll: () => api.get('/transaction-rules'),
  create: (data) => api.post('/transaction-rules', data),
  update: (id, data) => api.put(`/transaction-rules/${id}`, data),
  delete: (id) => api.delete(`/transaction-rules/${id}`),
};


export const allocationsApi = {
  getSuggestions: () => api.get('/allocations/suggestions'),
  confirm: (id) => api.post(`/allocations/${id}/confirm`),
  reject: (id) => api.post(`/allocations/${id}/reject`),
  update: (id, data) => api.patch(`/allocations/${id}`, data),
};

export const searchApi = {
  globalSearch: (q) => api.get('/search', { params: { q } }),
};

export const pageTemplatesApi = {
  list: (type) => api.get('/page-templates', { params: type ? { type } : {} }),
  get: (id) => api.get(`/page-templates/${id}`),
  create: (data) => api.post('/page-templates', data),
  update: (id, data) => api.put(`/page-templates/${id}`, data),
  delete: (id) => api.delete(`/page-templates/${id}`),
  getVariables: () => api.get('/page-templates/variables'),
  preview: (id, req) => api.post(`/page-templates/${id}/preview`, req),
  exportHtml: (id, req) => api.post(`/page-templates/${id}/export-html`, req, { responseType: 'blob' }),
  exportPdf: (id, req) => api.post(`/page-templates/${id}/export-pdf`, req, { responseType: 'blob' }),
  send: (id, req) => api.post(`/page-templates/${id}/send`, req),
  composePreview:  (req) => api.post('/page-templates/compose-preview', req),
  composeAndSend:  (req) => api.post('/page-templates/compose-and-send', req),
  pingenStatus: () => api.get('/page-templates/pingen/status'),
  pingenPriceEstimate: (params) => api.get('/page-templates/pingen/price-estimate', { params }),
};

export const sentDocumentsApi = {
  list: (params) => api.get('/sent-documents', { params }),
  get: (id) => api.get(`/sent-documents/${id}`),
  // Fetch rendered HTML with auth header (avoids 403 from window.open)
  getContent: (id) => api.get(`/sent-documents/${id}/content`, { responseType: 'text' }),
  refreshPingenStatus: (id) => api.post(`/sent-documents/${id}/refresh-pingen-status`),
  delete: (id) => api.delete(`/sent-documents/${id}`),
};

export const templateDraftsApi = {
  list: () => api.get('/template-drafts'),
  save: (data) => api.post('/template-drafts', data),
  get: (id) => api.get(`/template-drafts/${id}`),
  delete: (id) => api.delete(`/template-drafts/${id}`),
};

export const monthlyClosingApi = {
  get: (month, year) => api.get('/monthly-closing', { params: { month, year } }),
  refresh: (id) => api.post(`/monthly-closing/${id}/refresh`),
  listItems: (id) => api.get(`/monthly-closing/${id}/items`),
  getItemDetail: (id, aptId) => api.get(`/monthly-closing/${id}/items/${aptId}`),
  updateItem: (id, aptId, data) => api.patch(`/monthly-closing/${id}/items/${aptId}`, data),
  closeMonth: (id) => api.post(`/monthly-closing/${id}/close`),
};

export const emailInboxApi = {
  threads: (params) => api.get('/email-inbox/threads', { params }),
  thread: (id) => api.get(`/email-inbox/threads/${id}`),
  reply: (id, body) => api.post(`/email-inbox/threads/${id}/reply`, { body }),
  forward: (id, data) => api.post(`/email-inbox/threads/${id}/forward`, data),
  assignApartment: (id, apartment_id) => api.post(`/email-inbox/threads/${id}/assign-apartment`, { apartment_id }),
  assignTicket: (id, ticket_id) => api.post(`/email-inbox/threads/${id}/assign-ticket`, { ticket_id }),
  createTicket: (id, data) => api.post(`/email-inbox/threads/${id}/create-ticket`, data),
  assignEmployee: (id, data) => api.post(`/email-inbox/threads/${id}/assign-employee`, data),
  close: (id) => api.post(`/email-inbox/threads/${id}/close`),
  reopen: (id) => api.post(`/email-inbox/threads/${id}/reopen`),
  archive: (id) => api.post(`/email-inbox/threads/${id}/archive`),
  sync: () => api.post('/email-inbox/sync'),
  stats: () => api.get('/email-inbox/stats'),
  apartmentThreads: (apartmentId) => api.get(`/email-inbox/apartments/${apartmentId}/threads`),
};

// Invitations
export const invitationsApi = {
  get: (token) => api.get(`/invitations/${token}`),
  accept: (token, data) => api.post(`/invitations/${token}/accept`, data),
};

// Owner Portal (external owner access)
export const ownerPortalApi = {
  getInvite: (token) => api.get(`/owner-portal/invites/${token}`),
  acceptInvite: (token, data) => api.post(`/owner-portal/invites/${token}/accept`, data),
  me: () => api.get('/owner-portal/me'),
  apartments: () => api.get('/owner-portal/apartments'),
  apartment: (id) => api.get(`/owner-portal/apartments/${id}`),
  transactions: (id, params) => api.get(`/owner-portal/apartments/${id}/transactions`, { params }),
  payments: (id, params) => api.get(`/owner-portal/apartments/${id}/payments`, { params }),
  operatingCosts: (id) => api.get(`/owner-portal/apartments/${id}/operating-costs`),
  documents: (id) => api.get(`/owner-portal/apartments/${id}/documents`),
  contact: (data) => api.post('/owner-portal/contact', data),
};

// Owner Portal administration (internal roles only)
export const ownerPortalAdminApi = {
  listGrants: () => api.get('/owner-portal/admin/grants'),
  createGrant: (data) => api.post('/owner-portal/admin/grants', data),
  updateGrant: (id, data) => api.patch(`/owner-portal/admin/grants/${id}`, data),
  resendGrant: (id) => api.post(`/owner-portal/admin/grants/${id}/resend`),
  revokeGrant: (id) => api.post(`/owner-portal/admin/grants/${id}/revoke`),
};

export default api;
